import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Edit2, Trash2, Upload, ExternalLink, ZoomIn, Printer, X, ArrowUpDown, Search, FileSpreadsheet, BookOpen, Image as ImageIcon, Wand2, Share2, Sparkles } from 'lucide-react';
import FilePickerDialog from './FilePickerDialog';
import BulkImageAttachDialog from './BulkImageAttachDialog';
import ShareCatalogDialog from './ShareCatalogDialog';
import ProductCardDialog from './ProductCardDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceList = ({ user, onBackToDashboard }) => {
  const [priceItems, setPriceItems] = useState([]);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogGenerating, setCatalogGenerating] = useState(false);
  const [catalogConfig, setCatalogConfig] = useState({
    columns: ['product_name', 'product_image', 'category', 'generic_name', 'packing', 'ptr_gst'],
    products_per_slide: 4,
    company_name: 'WINLIFE PHARMA',
    category_wise: false,
    selected_manufacturers: ['all'],
    selected_categories: ['all']
  });
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [selectedManufacturers, setSelectedManufacturers] = useState(['all']);
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [selectedPriceColumn, setSelectedPriceColumn] = useState('pts');
  const [zoomedImage, setZoomedImage] = useState(null);
  const [pickerProductName, setPickerProductName] = useState(null);
  const [bulkAttachOpen, setBulkAttachOpen] = useState(false);
  const [shareCatalogOpen, setShareCatalogOpen] = useState(false);
  const [cardProductName, setCardProductName] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showManufacturerDropdown, setShowManufacturerDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100); // Show 100 items per page
  const fileInputRef = useRef(null);
  
  // Available columns for print selection (PTS+GST removed from print options)
  const availablePrintColumns = [
    { key: 'serial', label: 'Serial No.', default: true },
    { key: 'product_name', label: 'Product Name', default: true },
    { key: 'packing', label: 'Packing', default: true },
    { key: 'manufacturer', label: 'Manufacturer', default: true },
    { key: 'category', label: 'Category', default: false },
    { key: 'generic_name', label: 'Generic Name', default: true },
    { key: 'ptr_gst', label: 'PTR + GST', default: false },
    { key: 'pte_usd', label: 'PTE (USD $)', default: false },
    { key: 'product_link', label: 'Product Link', default: false },
    { key: 'product_image', label: 'Image', default: false }
  ];
  
  // Initialize selected print columns with defaults
  const [selectedPrintColumns, setSelectedPrintColumns] = useState(
    availablePrintColumns.filter(col => col.default).map(col => col.key)
  );
  
  // Category-wise print option
  const [categoryWisePrint, setCategoryWisePrint] = useState(false);

  const fetchPriceList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/price-list`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setPriceItems(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch price list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriceList();
  }, []);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedManufacturers, selectedCategories, searchTerm, sortConfig]);

  const handleEdit = (item) => {
    setEditingItem(item.product_name);
    setEditFormData({
      category: item.category || '',
      generic_name: item.generic_name || '',
      pts_gst: item.pts_gst || '',
      ptr_gst: item.ptr_gst || '',
      pte_usd: item.pte_usd || '',
      product_link: item.product_link || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditFormData({});
  };

  const handleSaveEdit = async (productName) => {
    try {
      // URL encode the product name to handle special characters like %
      const encodedProductName = encodeURIComponent(productName);
      
      // Clean up numeric fields - convert empty strings to 0 or parse to float
      const cleanedFormData = {
        ...editFormData,
        pts_gst: editFormData.pts_gst === '' ? 0 : parseFloat(editFormData.pts_gst) || 0,
        ptr_gst: editFormData.ptr_gst === '' ? 0 : parseFloat(editFormData.ptr_gst) || 0,
        pte_usd: editFormData.pte_usd === '' ? 0 : parseFloat(editFormData.pte_usd) || 0
      };
      
      await axios.put(`${API}/price-list/${encodedProductName}`, cleanedFormData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Price list updated');
      setEditingItem(null);
      fetchPriceList();
    } catch (error) {
      console.error('Save error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to update';
      
      // Handle validation errors (array of error objects)
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        errorMessage = errorDetail.msg || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (productName) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/price-list/${productName}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Item deleted');
      fetchPriceList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleImageUpload = async (productName, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/price-list/upload-image/${productName}`, formData, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Image uploaded & saved to Media Library');
      fetchPriceList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    }
  };

  // Picker callback for product image: upload OR attach from media library
  const handleImagePickerResult = async ({ source, file, libraryItem }) => {
    const productName = pickerProductName;
    if (!productName) return;
    if (source === 'upload' && file) {
      await handleImageUpload(productName, file);
    } else if (source === 'library' && libraryItem) {
      try {
        await axios.post(
          `${API}/price-list/attach-image-from-library/${encodeURIComponent(productName)}`,
          { library_id: libraryItem.id },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        toast.success(`Image "${libraryItem.original_name}" attached from Media Library`);
        fetchPriceList();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to attach image');
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/template/download/price-list`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Price_List_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleGenerateCatalog = async () => {
    setCatalogGenerating(true);
    try {
      // Determine logo URL based on catalog manufacturer selection
      const catalogManufacturers = catalogConfig.selected_manufacturers;
      const isPBLabsOnly = catalogManufacturers.length === 1 && catalogManufacturers[0] === 'PB LABS';
      // Send relative path - backend will read from frontend/public folder
      const catalogLogoUrl = isPBLabsOnly
        ? '/powerbolic-logo.jpeg'
        : '/winlife-logo.jpeg';
      
      const response = await axios.post(`${API}/price-list/generate-catalog`, {
        columns: catalogConfig.columns,
        manufacturer_filter: catalogConfig.selected_manufacturers.includes('all') ? null : catalogConfig.selected_manufacturers,
        category_filter: catalogConfig.selected_categories.includes('all') ? null : catalogConfig.selected_categories,
        company_name: catalogConfig.company_name,
        products_per_slide: catalogConfig.products_per_slide,
        category_wise: catalogConfig.category_wise,
        logo_url: catalogLogoUrl
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${catalogConfig.company_name.replace(/\s+/g, '_')}_Catalog.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Catalog generated! Open in PowerPoint or upload to Google Slides');
      setShowCatalogModal(false);
    } catch (error) {
      console.error('Catalog generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate catalog');
    } finally {
      setCatalogGenerating(false);
    }
  };

  const handlePrint = () => {
    // Filter items based on selected manufacturers and categories
    let filteredItems = priceItems;
    if (!selectedManufacturers.includes('all')) {
      filteredItems = filteredItems.filter(item => selectedManufacturers.includes(item.manufacturer));
    }
    if (!selectedCategories.includes('all')) {
      filteredItems = filteredItems.filter(item => selectedCategories.includes(item.category));
    }
    
    // Determine if multiple manufacturers are selected
    const isMultipleManufacturers = selectedManufacturers.length > 1 || 
      (selectedManufacturers.length === 1 && selectedManufacturers[0] === 'all');

    // Determine title based on selected price columns
    let priceListTitle = 'Price List';
    if (selectedPrintColumns.includes('pte_usd') && !selectedPrintColumns.includes('pts_gst') && !selectedPrintColumns.includes('ptr_gst')) {
      priceListTitle = 'Export Price List';
    } else if (selectedPrintColumns.includes('ptr_gst') && !selectedPrintColumns.includes('pts_gst') && !selectedPrintColumns.includes('pte_usd')) {
      priceListTitle = 'Stockist Price List';
    } else if (selectedPrintColumns.includes('pts_gst') && !selectedPrintColumns.includes('ptr_gst') && !selectedPrintColumns.includes('pte_usd')) {
      priceListTitle = 'Distribution Price List';
    }

    // Determine which logo and header to use based on manufacturer selection
    const isPBLabsOnly = selectedManufacturers.length === 1 && selectedManufacturers[0] === 'PB labs';
    const logoUrl = (isPBLabsOnly && !isMultipleManufacturers)
      ? `${window.location.origin}/powerbolic-logo.jpeg`
      : `${window.location.origin}/winlife-logo.jpeg`;
    const companyName = (isPBLabsOnly && !isMultipleManufacturers) ? 'POWERBOLIC LABS' : 'WINLIFE PHARMA';
    const currentDate = new Date().toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    
    // Generate table headers based on selected columns
    const getColumnClass = (col) => {
      const classMap = {
        'serial': 'col-serial',
        'product_name': 'col-product',
        'packing': 'col-packing',
        'manufacturer': 'col-manufacturer',
        'category': 'col-category',
        'generic_name': 'col-generic',
        'pts_gst': 'col-price',
        'ptr_gst': 'col-price',
        'pte_usd': 'col-price',
        'product_link': 'col-link',
        'product_image': 'col-image'
      };
      return classMap[col] || '';
    };
    
    const generateHeaders = () => {
      return selectedPrintColumns.map(col => {
        const colInfo = availablePrintColumns.find(c => c.key === col);
        const colClass = getColumnClass(col);
        return `<th class="${colClass}">${colInfo ? colInfo.label : col}</th>`;
      }).join('');
    };
    
    // Generate table row for each item
    const generateRow = (item, index) => {
      return selectedPrintColumns.map(col => {
        const colClass = getColumnClass(col);
        switch(col) {
          case 'serial': return `<td class="${colClass}">${index + 1}</td>`;
          case 'product_name': return `<td class="${colClass}">${item.product_name || '-'}</td>`;
          case 'packing': return `<td class="${colClass}">${item.packing || '-'}</td>`;
          case 'manufacturer': return `<td class="${colClass}">${item.manufacturer || '-'}</td>`;
          case 'category': return `<td class="${colClass}">${item.category || '-'}</td>`;
          case 'generic_name': return `<td class="${colClass}">${item.generic_name || '-'}</td>`;
          case 'pts_gst': return `<td class="${colClass}">${item.pts_gst || '-'}</td>`;
          case 'ptr_gst': return `<td class="${colClass}">${item.ptr_gst || '-'}</td>`;
          case 'pte_usd': return `<td class="${colClass}">${item.pte_usd ? '$' + item.pte_usd : '-'}</td>`;
          case 'product_link': return `<td class="${colClass}">${item.product_link ? '<a href="' + item.product_link + '" target="_blank" style="color: #1e40af;">Link</a>' : '-'}</td>`;
          case 'product_image': return `<td class="${colClass}">${item.product_image ? '<img src="' + item.product_image + '" style="width: 40px; height: 40px; object-fit: contain; border: 1px solid #ddd;" />' : '-'}</td>`;
          default: return `<td class="${colClass}">-</td>`;
        }
      }).join('');
    };
    
    // Generate normal table (non-category-wise)
    const generateNormalTable = () => {
      return `
        <table>
          <thead>
            <tr>
              ${generateHeaders()}
            </tr>
          </thead>
          <tbody>
            ${filteredItems.map((item, index) => `
              <tr>
                ${generateRow(item, index)}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    };
    
    // Generate category-wise content
    const generateCategoryWiseContent = () => {
      // Group items by category
      const groupedByCategory = {};
      filteredItems.forEach(item => {
        const cat = item.category || 'Uncategorized';
        if (!groupedByCategory[cat]) {
          groupedByCategory[cat] = [];
        }
        groupedByCategory[cat].push(item);
      });
      
      // Sort categories alphabetically, but put 'Uncategorized' at the end
      const sortedCategories = Object.keys(groupedByCategory).sort((a, b) => {
        if (a === 'Uncategorized') return 1;
        if (b === 'Uncategorized') return -1;
        return a.localeCompare(b);
      });
      
      let serialCounter = 0;
      
      // Generate HTML for each category
      return sortedCategories.map(category => {
        const categoryItems = groupedByCategory[category];
        const categoryRows = categoryItems.map((item) => {
          serialCounter++;
          const rowCells = selectedPrintColumns.map(col => {
            const colClass = getColumnClass(col);
            switch(col) {
              case 'serial': return '<td class="' + colClass + '">' + serialCounter + '</td>';
              case 'product_name': return '<td class="' + colClass + '">' + (item.product_name || '-') + '</td>';
              case 'packing': return '<td class="' + colClass + '">' + (item.packing || '-') + '</td>';
              case 'manufacturer': return '<td class="' + colClass + '">' + (item.manufacturer || '-') + '</td>';
              case 'category': return '<td class="' + colClass + '">' + (item.category || '-') + '</td>';
              case 'generic_name': return '<td class="' + colClass + '">' + (item.generic_name || '-') + '</td>';
              case 'pts_gst': return '<td class="' + colClass + '">' + (item.pts_gst || '-') + '</td>';
              case 'ptr_gst': return '<td class="' + colClass + '">' + (item.ptr_gst || '-') + '</td>';
              case 'pte_usd': return '<td class="' + colClass + '">' + (item.pte_usd ? '$' + item.pte_usd : '-') + '</td>';
              case 'product_link': return '<td class="' + colClass + '">' + (item.product_link ? '<a href="' + item.product_link + '" target="_blank" style="color: #1e40af;">Link</a>' : '-') + '</td>';
              case 'product_image': return '<td class="' + colClass + '">' + (item.product_image ? '<img src="' + item.product_image + '" style="width: 40px; height: 40px; object-fit: contain; border: 1px solid #ddd;" />' : '-') + '</td>';
              default: return '<td class="' + colClass + '">-</td>';
            }
          }).join('');
          return '<tr>' + rowCells + '</tr>';
        }).join('');
        
        return `
          <div class="category-section" style="margin-bottom: 25px;">
            <h3 style="font-size: 15px; font-weight: bold; color: #1e3a5f; margin: 15px 0 10px 0; padding: 8px 12px; background: #e8f4fd; border-left: 4px solid #1e40af; page-break-after: avoid;">
              ${category}
            </h3>
            <table>
              <thead>
                <tr>
                  ${generateHeaders()}
                </tr>
              </thead>
              <tbody>
                ${categoryRows}
              </tbody>
            </table>
          </div>
        `;
      }).join('');
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${companyName} - Price List</title>
          <style>
            * {
              box-sizing: border-box;
            }
            
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 0;
            }
            
            /* Print Header - appears on every page */
            .print-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 15px 20px;
              border-bottom: 2px solid #333;
              background: white;
            }
            
            .header-left {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            
            .logo {
              height: 60px;
              object-fit: contain;
            }
            
            .company-info h1 {
              font-size: 24px;
              font-weight: bold;
              margin: 0;
              color: #1e40af;
            }
            
            .company-info p {
              font-size: 12px;
              margin: 3px 0 0 0;
              color: #666;
            }
            
            .header-right {
              text-align: right;
            }
            
            .header-right .label {
              font-size: 11px;
              font-weight: bold;
              margin: 0;
              color: #333;
            }
            
            .header-right .date {
              font-size: 14px;
              margin: 3px 0 0 0;
              color: #1e40af;
              font-weight: bold;
            }
            
            /* Content area */
            .content {
              padding: 10px 20px;
            }
            
            /* Table styles */
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
              table-layout: fixed;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px 8px; 
              text-align: left;
              font-size: 11px;
              overflow: hidden;
              text-overflow: ellipsis;
              word-wrap: break-word;
            }
            
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
            }
            
            /* Fixed column widths for uniform appearance */
            .col-serial { width: 50px; min-width: 50px; max-width: 50px; }
            .col-product { width: 200px; min-width: 200px; }
            .col-packing { width: 80px; min-width: 80px; max-width: 80px; }
            .col-manufacturer { width: 120px; min-width: 120px; }
            .col-category { width: 100px; min-width: 100px; }
            .col-generic { width: 180px; min-width: 180px; }
            .col-price { width: 80px; min-width: 80px; max-width: 80px; text-align: right; }
            .col-link { width: 60px; min-width: 60px; max-width: 60px; text-align: center; }
            .col-image { width: 60px; min-width: 60px; max-width: 60px; text-align: center; }
            
            /* Footer with page numbers */
            .page-footer {
              position: fixed;
              bottom: 0;
              right: 20px;
              left: 20px;
              height: 30px;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              border-top: 1px solid #ddd;
              padding-top: 5px;
              background: white;
            }
            
            .page-number {
              font-size: 11px;
              color: #666;
            }
            
            /* Print-specific styles */
            @media print {
              @page {
                size: A4;
                margin: 80px 15px 40px 15px;
                @bottom-right {
                  content: "Page " counter(page) " of " counter(pages);
                }
              }
              
              body {
                margin: 0;
                padding: 0;
              }
              
              /* Header repeats on every page */
              thead {
                display: table-header-group;
              }
              
              .print-header {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 1000;
              }
              
              .page-footer {
                position: fixed;
                bottom: 0;
                display: flex;
                justify-content: flex-end;
              }
              
              .content {
                margin-top: 100px;
              }
              
              /* Hide the manual page number, use @page counter instead */
              .pageNumber::before {
                content: counter(page);
              }
              
              /* Avoid breaking table rows */
              tr {
                page-break-inside: avoid;
              }
            }
            
            /* Screen view */
            @media screen {
              .page-footer {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <!-- Print Header - Repeats on every page -->
          <div class="print-header">
            <div class="header-left">
              <img src="${logoUrl}" alt="${companyName} Logo" class="logo" />
              <div class="company-info">
                <h1>${companyName}</h1>
                <p>Price List</p>
              </div>
            </div>
            <div class="header-right">
              <p class="label">Price Effective Date</p>
              <p class="date">${currentDate}</p>
            </div>
          </div>

          <!-- Content Area -->
          <div class="content">
            <!-- Title on first page only -->
            <div style="text-align: center; margin-bottom: 20px; page-break-after: avoid;">
              <h2 style="font-size: 20px; font-weight: bold; color: #1e40af; margin: 0;">${priceListTitle}</h2>
            </div>
            
            ${categoryWisePrint ? generateCategoryWiseContent() : generateNormalTable()}
          </div>
          
          <!-- Footer with page numbers -->
          <div class="page-footer">
            <span class="page-number">Page <span class="pageNumber"></span></span>
          </div>
          <script>
            // Set page number using JavaScript
            document.addEventListener('DOMContentLoaded', function() {
              var pageNum = document.querySelector('.pageNumber');
              if (pageNum) {
                pageNum.textContent = '1';
              }
            });
            
            window.onload = function() { 
              // Images are loaded, user can manually print or export
              // Removed auto-print - user can click Print button in the new tab
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // New function to open report in new tab with PDF/Excel options
  const openPriceListReport = () => {
    // Filter items based on selected manufacturers and categories
    let filteredItems = priceItems;
    if (!selectedManufacturers.includes('all')) {
      filteredItems = filteredItems.filter(item => selectedManufacturers.includes(item.manufacturer));
    }
    if (!selectedCategories.includes('all')) {
      filteredItems = filteredItems.filter(item => selectedCategories.includes(item.category));
    }
    
    // Determine company name from selected manufacturer
    let companyName = 'WINLIFE PHARMA';
    if (selectedManufacturers.length === 1 && selectedManufacturers[0] !== 'all') {
      companyName = selectedManufacturers[0];
    }
    
    // Store data in localStorage and open new tab
    const reportData = {
      items: filteredItems,
      company: companyName,
      selectedColumns: selectedPrintColumns
    };
    localStorage.setItem('priceListReportData', JSON.stringify(reportData));
    window.open('/price-list-report', '_blank');
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const manufacturers = ['all', ...new Set(priceItems.map(item => item.manufacturer).filter(Boolean))];
  const categories = ['all', ...new Set(priceItems.map(item => item.category).filter(Boolean))];
  
  const filteredAndSortedItems = useMemo(() => {
    let items = priceItems;
    
    // Filter by manufacturers
    if (!selectedManufacturers.includes('all')) {
      items = items.filter(item => selectedManufacturers.includes(item.manufacturer));
    }
    
    // Filter by categories
    if (!selectedCategories.includes('all')) {
      items = items.filter(item => selectedCategories.includes(item.category));
    }

    // Then filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      items = items.filter(item =>
        item.product_name?.toLowerCase().includes(search) ||
        item.manufacturer?.toLowerCase().includes(search) ||
        item.generic_name?.toLowerCase().includes(search)
      );
    }

    // Then sort
    if (sortConfig.key) {
      items = [...items].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return items;
  }, [priceItems, selectedManufacturers, selectedCategories, sortConfig, searchTerm]);

  // Paginate items for better performance
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedItems.slice(startIndex, endIndex);
  }, [filteredAndSortedItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const filteredItems = paginatedItems;

  const handleFileUpload = async (e, replace = false) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/price-list/upload?replace=${replace}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      fetchPriceList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleUploadClick = () => {
    const replace = window.confirm(
      'Do you want to REPLACE all existing data?\n\n' +
      'Click OK to REPLACE (delete old data)\n' +
      'Click Cancel to ADD to existing data'
    );
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => handleFileUpload(e, replace);
    input.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-600">Loading price list...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 rounded-xl -mx-6 -mt-6 mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Price List</h2>
          <p className="text-slate-300 mt-1">Manage product prices and catalog</p>
        </div>
        {onBackToDashboard && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white hover:bg-white/30 border border-white/30 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
        )}
      </div>

      {/* Search and Upload */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Search className="w-5 h-5 text-slate-600 hidden sm:block" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64"
            />
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm h-9"
            >
              <FileSpreadsheet className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Download </span>Template
            </Button>
            <Button
              onClick={handleUploadClick}
              variant="outline"
              size="sm"
              disabled={uploading}
              className="flex-1 sm:flex-none text-xs sm:text-sm h-9"
            >
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{uploading ? 'Uploading...' : 'Upload Excel'}</span>
              <span className="sm:hidden">{uploading ? '...' : 'Upload'}</span>
            </Button>
            <Button
              onClick={() => setShowCatalogModal(true)}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm h-9 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            >
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Create </span>Catalog
            </Button>
            <Button
              onClick={() => setShareCatalogOpen(true)}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm h-9 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              data-testid="share-catalog-btn"
              title="Generate a shareable PDF catalog for WhatsApp, email, or download"
            >
              <Share2 className="w-4 h-4 sm:mr-2" />
              Share
            </Button>
            {(user?.role === 'admin' || user?.role === 'winlife') && (
              <Button
                onClick={() => setBulkAttachOpen(true)}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none text-xs sm:text-sm h-9 bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                data-testid="bulk-attach-images-btn"
                title="Auto-match images from Media Library to products by filename"
              >
                <Wand2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Bulk Attach </span>Images
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Print Controls */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-start sm:items-center gap-2 sm:gap-4 w-full lg:w-auto">
          <div className="relative w-full sm:w-auto">
            <Label className="text-[10px] sm:text-xs text-slate-600 mb-1 block">Filter by Manufacturer</Label>
            <button
              onClick={() => setShowManufacturerDropdown(!showManufacturerDropdown)}
              className="w-full sm:w-56 px-3 py-2 text-left border border-slate-300 rounded-lg bg-white hover:bg-slate-50 flex items-center justify-between text-sm"
            >
              <span className="text-xs sm:text-sm truncate">
                {selectedManufacturers.includes('all') 
                  ? 'All Manufacturers' 
                  : `${selectedManufacturers.length} selected`}
              </span>
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showManufacturerDropdown && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {manufacturers.map(mnf => {
                    const isSelected = selectedManufacturers.includes(mnf);
                    const isAll = mnf === 'all';
                    
                    return (
                      <label
                        key={mnf}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (isAll) {
                              // If "All" is clicked, select only "All"
                              setSelectedManufacturers(['all']);
                            } else {
                              // If a specific manufacturer is clicked
                              if (e.target.checked) {
                                // Add to selection and remove "all"
                                const newSelection = selectedManufacturers.filter(m => m !== 'all');
                                setSelectedManufacturers([...newSelection, mnf]);
                              } else {
                                // Remove from selection
                                const newSelection = selectedManufacturers.filter(m => m !== mnf);
                                // If nothing selected, default to "all"
                                setSelectedManufacturers(newSelection.length === 0 ? ['all'] : newSelection);
                              }
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm">
                          {isAll ? 'All Manufacturers' : mnf}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-slate-200 p-2">
                  <Button
                    onClick={() => setShowManufacturerDropdown(false)}
                    size="sm"
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Category Dropdown */}
          <div className="relative">
            <Label className="text-xs text-slate-600 mb-1 block">Filter by Category (Multi-select)</Label>
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-56 px-3 py-2 text-left border border-slate-300 rounded-lg bg-white hover:bg-slate-50 flex items-center justify-between"
            >
              <span className="text-sm truncate">
                {selectedCategories.includes('all') 
                  ? 'All Categories' 
                  : `${selectedCategories.length} selected`}
              </span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showCategoryDropdown && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {categories.map(cat => {
                    const isSelected = selectedCategories.includes(cat);
                    const isAll = cat === 'all';
                    
                    return (
                      <label
                        key={cat}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (isAll) {
                              setSelectedCategories(['all']);
                            } else {
                              if (e.target.checked) {
                                const newSelection = selectedCategories.filter(c => c !== 'all');
                                setSelectedCategories([...newSelection, cat]);
                              } else {
                                const newSelection = selectedCategories.filter(c => c !== cat);
                                setSelectedCategories(newSelection.length === 0 ? ['all'] : newSelection);
                              }
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm">
                          {isAll ? 'All Categories' : cat}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-slate-200 p-2">
                  <Button
                    onClick={() => setShowCategoryDropdown(false)}
                    size="sm"
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Column Selection Dropdown */}
          <div className="relative">
            <Label className="text-xs text-slate-600 mb-1 block">Select Columns to Print</Label>
            <button
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              className="w-64 px-3 py-2 text-left border border-slate-300 rounded-lg bg-white hover:bg-slate-50 flex items-center justify-between"
            >
              <span className="text-sm truncate">
                {selectedPrintColumns.length === availablePrintColumns.length 
                  ? 'All Columns' 
                  : `${selectedPrintColumns.length} columns selected`}
              </span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showColumnDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-300 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {/* Select All / Deselect All */}
                  <div className="flex gap-2 pb-2 border-b border-slate-200">
                    <button
                      onClick={() => setSelectedPrintColumns(availablePrintColumns.map(c => c.key))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => setSelectedPrintColumns(['serial', 'product_name'])}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Reset to Default
                    </button>
                  </div>
                  
                  {availablePrintColumns.map(col => {
                    const isSelected = selectedPrintColumns.includes(col.key);
                    
                    return (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPrintColumns([...selectedPrintColumns, col.key]);
                            } else {
                              // Ensure at least serial and product_name are selected
                              if (col.key === 'serial' || col.key === 'product_name') {
                                return; // Don't allow deselecting these
                              }
                              setSelectedPrintColumns(selectedPrintColumns.filter(c => c !== col.key));
                            }
                          }}
                          disabled={col.key === 'serial' || col.key === 'product_name'}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={`text-sm ${(col.key === 'serial' || col.key === 'product_name') ? 'text-slate-400' : ''}`}>
                          {col.label}
                          {(col.key === 'serial' || col.key === 'product_name') && 
                            <span className="text-xs text-slate-400 ml-1">(required)</span>
                          }
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-slate-200 p-2">
                  <Button
                    onClick={() => setShowColumnDropdown(false)}
                    size="sm"
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Category-wise Print Option */}
          <div className="flex items-center">
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-100">
              <input
                type="checkbox"
                checked={categoryWisePrint}
                onChange={(e) => setCategoryWisePrint(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-slate-700">Category-wise Print</span>
            </label>
          </div>
        </div>

        <Button onClick={openPriceListReport} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto h-10" data-testid="print-button">
          <Printer className="w-4 h-4 mr-2" />
          Print Price List
        </Button>
      </div>

      {/* Price List Table */}
      <div className="mb-2 flex items-center space-x-2 text-xs sm:text-sm text-slate-600">
        <ArrowUpDown className="w-3 h-3 sm:w-4 sm:h-4" />
        <span>Click column headers to sort</span>
      </div>
      
      {/* Scrollable Table View */}
      <div className="overflow-x-auto rounded-lg border border-slate-200" style={{ maxHeight: '65vh' }}>
        <table className="w-full" data-testid="price-list-table">
          <thead className="sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-200 shadow-sm">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>S.No</th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('product_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Product Name</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('packing')}
              >
                <div className="flex items-center space-x-1">
                  <span>Packing</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('manufacturer')}
              >
                <div className="flex items-center space-x-1">
                  <span>MNF</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center space-x-1">
                  <span>Category</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('generic_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Generic Name</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('pts_gst')}
              >
                <div className="flex items-center space-x-1">
                  <span>PTS + GST</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('ptr_gst')}
              >
                <div className="flex items-center space-x-1">
                  <span>PTR + GST</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase cursor-pointer hover:bg-slate-100" 
                style={{ fontSize: '10px' }}
                onClick={() => handleSort('pte_usd')}
              >
                <div className="flex items-center space-x-1">
                  <span>PTE (USD $)</span>
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Product Link</th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Image</th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {filteredItems.map((item, index) => {
              const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
              return (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors" data-testid={`price-row-${index}`}>
                <td className="px-2 py-2 text-xs text-slate-700">{serialNumber}</td>
                <td className="px-2 py-2 text-xs text-slate-800 font-medium">{item.product_name}</td>
                <td className="px-2 py-2 text-xs text-slate-700">{item.packing || '-'}</td>
                <td className="px-2 py-2 text-xs text-slate-700">{item.manufacturer || '-'}</td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                      placeholder="Enter category"
                      className="h-7 text-xs"
                    />
                  ) : (
                    <span className="text-slate-700">{item.category || '-'}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      value={editFormData.generic_name}
                      onChange={(e) => setEditFormData({...editFormData, generic_name: e.target.value})}
                      className="h-7 text-xs"
                    />
                  ) : (
                    <span className="text-slate-700">{item.generic_name || '-'}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      type="number"
                      value={editFormData.pts_gst}
                      onChange={(e) => setEditFormData({...editFormData, pts_gst: e.target.value})}
                      className="h-7 text-xs"
                    />
                  ) : (
                    <span className="text-slate-700">{item.pts_gst || '-'}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      type="number"
                      value={editFormData.ptr_gst}
                      onChange={(e) => setEditFormData({...editFormData, ptr_gst: e.target.value})}
                      className="h-7 text-xs"
                    />
                  ) : (
                    <span className="text-slate-700">{item.ptr_gst || '-'}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      type="number"
                      value={editFormData.pte_usd}
                      onChange={(e) => setEditFormData({...editFormData, pte_usd: e.target.value})}
                      className="h-7 text-xs"
                    />
                  ) : (
                    <span className="text-slate-700">{item.pte_usd ? `$${item.pte_usd}` : '-'}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">
                  {editingItem === item.product_name ? (
                    <Input
                      value={editFormData.product_link}
                      onChange={(e) => setEditFormData({...editFormData, product_link: e.target.value})}
                      placeholder="https://..."
                      className="h-7 text-xs"
                    />
                  ) : item.product_link ? (
                    <a href={item.product_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Link
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {item.product_image ? (
                    <div className="flex items-center space-x-1">
                      <img 
                        src={item.product_image} 
                        alt="Product" 
                        className="w-10 h-10 object-cover rounded cursor-pointer border border-slate-300"
                        onClick={() => setZoomedImage(item.product_image)}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setZoomedImage(item.product_image)}
                        className="h-6 px-1"
                      >
                        <ZoomIn className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPickerProductName(item.product_name)}
                        className="h-6 px-1"
                        title="Change image"
                        data-testid={`price-img-change-${item.product_name}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPickerProductName(item.product_name)}
                      className="h-7 text-xs"
                      data-testid={`price-img-add-${item.product_name}`}
                    >
                      <ImageIcon className="w-3 h-3 mr-1" />
                      Add Image
                    </Button>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex space-x-1">
                    {editingItem === item.product_name ? (
                      <>
                        <Button
                          onClick={() => handleSaveEdit(item.product_name)}
                          size="sm"
                          className="h-7 px-2 bg-green-600 hover:bg-green-700"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => setCardProductName(item.product_name)}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100"
                          data-testid={`share-card-${index}`}
                          title="Generate social-share card (1080×1080 PNG)"
                        >
                          <Sparkles className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(item)}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2"
                          data-testid={`edit-price-${index}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(item.product_name)}
                          size="sm"
                          variant="destructive"
                          className="h-7 px-2"
                          data-testid={`delete-price-${index}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedItems.length)} of {filteredAndSortedItems.length} items
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              size="sm"
              variant="outline"
            >
              First
            </Button>
            <Button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              size="sm"
              variant="outline"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              size="sm"
              variant="outline"
            >
              Next
            </Button>
            <Button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              size="sm"
              variant="outline"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files[0];
          const productName = e.target.dataset.productName;
          if (file && productName) {
            handleImageUpload(productName, file);
          }
        }}
      />

      {/* Smart Image Picker — Upload New OR Pick from Media Library */}
      <FilePickerDialog
        open={!!pickerProductName}
        onClose={() => setPickerProductName(null)}
        user={user}
        kind="image"
        title={`Set product image${pickerProductName ? ` — ${pickerProductName}` : ''}`}
        onPick={handleImagePickerResult}
      />

      {/* Bulk Attach Images from Media Library */}
      <BulkImageAttachDialog
        open={bulkAttachOpen}
        onClose={() => setBulkAttachOpen(false)}
        user={user}
        products={priceItems}
        onApplied={fetchPriceList}
      />

      {/* Share Catalog — generate sharable PDF for any platform */}
      <ShareCatalogDialog
        open={shareCatalogOpen}
        onClose={() => setShareCatalogOpen(false)}
        user={user}
        products={priceItems}
      />

      {/* Single-Product Card — 1080×1080 PNG for social media */}
      <ProductCardDialog
        open={!!cardProductName}
        onClose={() => setCardProductName(null)}
        user={user}
        productName={cardProductName}
      />

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <Button
              onClick={() => setZoomedImage(null)}
              className="absolute top-2 right-2 bg-white text-slate-800 hover:bg-slate-100"
              size="sm"
            >
              <X className="w-4 h-4" />
            </Button>
            <img 
              src={zoomedImage} 
              alt="Product Zoomed" 
              className="max-w-full max-h-screen object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Catalog Generation Modal */}
      {showCatalogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6" />
                  <h3 className="text-xl font-bold">Generate Product Catalog</h3>
                </div>
                <button 
                  onClick={() => setShowCatalogModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-purple-100 text-sm mt-1">Create a PowerPoint presentation for your products</p>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Company Name with Logo Preview */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Company Name (Header)</Label>
                  <Input
                    value={catalogConfig.company_name}
                    onChange={(e) => setCatalogConfig({...catalogConfig, company_name: e.target.value})}
                    placeholder="WINLIFE PHARMA"
                    className="h-10"
                  />
                </div>
                {/* Logo Preview */}
                <div className="w-24">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Logo</Label>
                  <div className="h-10 border rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                    <img 
                      src={catalogConfig.selected_manufacturers.length === 1 && catalogConfig.selected_manufacturers[0] === 'PB LABS'
                        ? '/powerbolic-logo.jpeg'
                        : '/winlife-logo.jpeg'
                      }
                      alt="Company Logo"
                      className="h-8 w-auto object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Manufacturer Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Select Manufacturers
                  <span className="text-xs text-slate-500 ml-2">(Logo auto-updates based on selection)</span>
                </Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-slate-50">
                  <div className="flex flex-wrap gap-2">
                    {manufacturers.map(mnf => (
                      <label
                        key={mnf}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm ${
                          catalogConfig.selected_manufacturers.includes(mnf)
                            ? 'border-purple-500 bg-purple-100 text-purple-700'
                            : 'border-slate-300 bg-white hover:bg-slate-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={catalogConfig.selected_manufacturers.includes(mnf)}
                          onChange={(e) => {
                            if (mnf === 'all') {
                              setCatalogConfig({
                                ...catalogConfig,
                                selected_manufacturers: e.target.checked ? ['all'] : []
                              });
                            } else {
                              let newSelection;
                              if (e.target.checked) {
                                newSelection = catalogConfig.selected_manufacturers.filter(m => m !== 'all');
                                newSelection.push(mnf);
                              } else {
                                newSelection = catalogConfig.selected_manufacturers.filter(m => m !== mnf);
                              }
                              if (newSelection.length === 0) newSelection = ['all'];
                              setCatalogConfig({
                                ...catalogConfig,
                                selected_manufacturers: newSelection
                              });
                            }
                          }}
                          className="hidden"
                        />
                        <span>{mnf === 'all' ? '✓ All Manufacturers' : mnf}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Category Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Categories</Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-slate-50">
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <label
                        key={cat}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm ${
                          catalogConfig.selected_categories.includes(cat)
                            ? 'border-green-500 bg-green-100 text-green-700'
                            : 'border-slate-300 bg-white hover:bg-slate-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={catalogConfig.selected_categories.includes(cat)}
                          onChange={(e) => {
                            if (cat === 'all') {
                              setCatalogConfig({
                                ...catalogConfig,
                                selected_categories: e.target.checked ? ['all'] : []
                              });
                            } else {
                              let newSelection;
                              if (e.target.checked) {
                                newSelection = catalogConfig.selected_categories.filter(c => c !== 'all');
                                newSelection.push(cat);
                              } else {
                                newSelection = catalogConfig.selected_categories.filter(c => c !== cat);
                              }
                              if (newSelection.length === 0) newSelection = ['all'];
                              setCatalogConfig({
                                ...catalogConfig,
                                selected_categories: newSelection
                              });
                            }
                          }}
                          className="hidden"
                        />
                        <span>{cat === 'all' ? '✓ All Categories' : cat}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Products per Slide */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Products per Slide</Label>
                <div className="flex gap-2">
                  {[1, 2, 4].map(num => (
                    <button
                      key={num}
                      onClick={() => setCatalogConfig({...catalogConfig, products_per_slide: num})}
                      className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                        catalogConfig.products_per_slide === num 
                          ? 'border-purple-600 bg-purple-50 text-purple-700' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {num} {num === 1 ? 'Product' : 'Products'}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Category-wise Option */}
              <div>
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all hover:bg-purple-50 ${catalogConfig.category_wise ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`}>
                  <input
                    type="checkbox"
                    checked={catalogConfig.category_wise}
                    onChange={(e) => setCatalogConfig({...catalogConfig, category_wise: e.target.checked})}
                    className="w-5 h-5 text-purple-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">Category-wise Catalog</span>
                    <p className="text-xs text-slate-500 mt-0.5">Group products by category with category headers on each section</p>
                  </div>
                </label>
              </div>
              
              {/* Column Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Select Columns to Include</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { key: 'product_name', label: 'Product Name', required: true },
                    { key: 'product_image', label: 'Product Image' },
                    { key: 'category', label: 'Category' },
                    { key: 'generic_name', label: 'Generic Name' },
                    { key: 'packing', label: 'Packing' },
                    { key: 'mrp', label: 'MRP' },
                    { key: 'ptr_gst', label: 'PTR + GST' },
                    { key: 'pte_usd', label: 'PTE (USD $)' },
                  ].map(col => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                        catalogConfig.columns.includes(col.key)
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      } ${col.required ? 'opacity-75' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={catalogConfig.columns.includes(col.key)}
                        disabled={col.required}
                        onChange={(e) => {
                          if (col.required) return;
                          if (e.target.checked) {
                            setCatalogConfig({
                              ...catalogConfig, 
                              columns: [...catalogConfig.columns, col.key]
                            });
                          } else {
                            setCatalogConfig({
                              ...catalogConfig, 
                              columns: catalogConfig.columns.filter(c => c !== col.key)
                            });
                          }
                        }}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-slate-700">{col.label}</span>
                      {col.required && <span className="text-xs text-slate-400">(required)</span>}
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Selection Summary */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Selection Summary:</p>
                <p>• Manufacturers: {catalogConfig.selected_manufacturers.includes('all') ? 'All' : catalogConfig.selected_manufacturers.join(', ')}</p>
                <p>• Categories: {catalogConfig.selected_categories.includes('all') ? 'All' : catalogConfig.selected_categories.join(', ')}</p>
                <p>• Products will be filtered based on your selection above</p>
              </div>
              
              {/* Image Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>📷 Image Source:</strong> Images will be fetched from Google Drive links or Product Image URLs. 
                  If no image is available, a placeholder will be shown which you can replace in PowerPoint/Google Slides.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 rounded-b-xl">
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowCatalogModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateCatalog}
                  disabled={catalogGenerating}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {catalogGenerating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-4 h-4 mr-2" />
                      Generate Catalog (.pptx)
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3 text-center">
                The file will open in Microsoft PowerPoint. You can also upload it to Google Slides for editing.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceList;
